/**
 * Materialising the flow's Iris branches into stored handoff rules on save.
 *
 * The canvas is where handoff is EDITED; the escalation-rules API is still
 * where it LIVES, because that is what ai-operations resolves and hands to the
 * calling service in the escalate webhook. So a save writes the flow, then
 * reconciles the rules it owns against what the canvas now says.
 *
 * OWNERSHIP IS THE WHOLE SAFETY STORY. This only ever creates, updates or
 * deletes rules it named itself (`FLOW_RULE_PREFIX`). A rule somebody wrote in
 * the old Handoff screen is never touched — it keeps working exactly as it did
 * until they choose to bring it onto the canvas. Reconciling everything would
 * mean the first save of any flow silently deleting handoff nobody had migrated.
 */
import { useCallback } from 'react';
import type { CallFlowStep, HandoffTrigger } from '@/lib/voice/call-flow-types';
import {
  useCreateEscalationRule,
  useDeleteEscalationRule,
  useEscalationRules,
  useUpdateEscalationRule,
  type EscalationRule,
} from '@/lib/ai/escalation-rules';
import {
  flowRuleBranch,
  flowRuleOwner,
  isImportable,
  rulesFromFlow,
  type MaterialisedRule,
} from '@/lib/ai/handoff-flow';

export interface HandoffSyncResult {
  created: number;
  updated: number;
  removed: number;
}

/** What `sync` would do, without doing it. Pure, so the UI can preview it. */
export function planHandoffSync(
  flowId: string,
  flowName: string,
  steps: readonly CallFlowStep[],
  existing: readonly EscalationRule[],
  /**
   * Rule id → the ring step the import drew for it. ADOPTION IS LIMITED TO
   * THESE, and only while the branch still points at that very step.
   *
   * It used to take any unowned rule answering the reason being wired, on the
   * reasoning that it was "almost always the one somebody just drew" — a guess
   * whose cost, when wrong, is an ordinary canvas edit silently overwriting a
   * live rule nobody asked to touch. Keying on the STEP rather than just the
   * rule closes the whole class rather than one route into it: discarding the
   * draft, or deleting the drawn step and wiring the reason somewhere else,
   * both leave a rule id that no longer describes anything on the canvas.
   * Review, #2880.
   */
  imported: ReadonlyMap<string, string> = new Map(),
) {
  const desired = rulesFromFlow(flowId, flowName, steps);

  // Owned by THIS flow, keyed on the id in the rule's name. Keyed on the name
  // itself, a rename left the old rules behind for ever and a short flow name
  // could claim a longer one's rules — and since rules are tried in priority
  // order, either left something stale or foreign winning. Review, #2880.
  const ownedByBranch = new Map<HandoffTrigger, EscalationRule>();
  for (const rule of existing) {
    if (flowRuleOwner(rule) !== flowId) continue;
    const branch = flowRuleBranch(rule);
    if (branch) ownedByBranch.set(branch, rule);
  }

  const create: MaterialisedRule[] = [];
  const update: { id: string; payload: MaterialisedRule }[] = [];
  const adopted = new Set<string>();

  for (const want of desired) {
    const mine = ownedByBranch.get(want.branch);
    if (mine) {
      update.push({ id: mine.id, payload: want });
      continue;
    }

    // ADOPTION, and only of a rule the operator explicitly drew. Creating a
    // second rule beside a drawn one would leave two answering the trigger,
    // with the older able to win on priority — so "the canvas takes over" would
    // be false. Updating one nobody drew is the opposite mistake.
    const legacy = existing.find(
      (r) =>
        imported.get(r.id) === want.ringStepId &&
        !adopted.has(r.id) &&
        flowRuleOwner(r) === null &&
        isImportable(r) &&
        flowRuleBranch(r) === want.branch,
    );
    if (legacy) {
      adopted.add(legacy.id);
      update.push({ id: legacy.id, payload: want });
      continue;
    }

    create.push(want);
  }

  const wanted = new Set(desired.map((d) => d.branch));
  const remove = [...ownedByBranch.entries()]
    .filter(([branch]) => !wanted.has(branch))
    .map(([, rule]) => rule);

  return { create, update, remove };
}

export function useHandoffSync() {
  const { data: existing = [] } = useEscalationRules();
  const create = useCreateEscalationRule();
  const update = useUpdateEscalationRule();
  const remove = useDeleteEscalationRule();

  return useCallback(
    async (
      flowId: string,
      flowName: string,
      steps: readonly CallFlowStep[],
      imported: ReadonlyMap<string, string> = new Map(),
    ): Promise<HandoffSyncResult> => {
      const plan = planHandoffSync(flowId, flowName, steps, existing, imported);

      // Sequential on purpose: these are a handful of writes against one
      // account-wide list, and firing them together has them racing to
      // invalidate the same query.
      for (const payload of plan.create) {
        const { branch: _branch, ringStepId: _step, ...body } = payload;
        await create.mutateAsync(body);
      }
      for (const { id, payload } of plan.update) {
        const { branch: _branch, ringStepId: _step, ...body } = payload;
        await update.mutateAsync({ id, ...body });
      }
      for (const rule of plan.remove) {
        await remove.mutateAsync(rule.id);
      }

      return {
        created: plan.create.length,
        updated: plan.update.length,
        removed: plan.remove.length,
      };
    },
    [existing, create, update, remove],
  );
}
